import isEmpty from 'lodash/isEmpty'

import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useNavigate, useParams } from 'react-router-dom'
import type { Location } from 'react-router-dom'
import type { State } from 'types/redux'
import {
  clientLibraryClientsResetErrors,
  inviteClientLibraryClients,
} from '@disco/state-management/actions/client-library-client-actions'
import mixpanel from '@disco/api/mixpanel'
import type { LibraryClientsInviteState } from '@disco/state-management/reducers/forms/library-clients-invite'
import { omit } from '@disco/utils/array'
import { deleteItem, updateItem } from '@disco/utils/immutable'
import Row from 'components/common/row'
import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ErrorText from 'components/ui/error-text'
import Heading from 'components/ui/heading'
import Label from 'components/ui/label'
import ModalWindow from 'components/ui/modal-window'
import TextField from 'components/ui/text-field'
const EMPTY_CLIENT = {
  email: '',
  first_name: '',
  last_name: '',
  errors: {},
}
type Client = {
  email: string
  first_name: string
  last_name: string
  errors: Record<string, boolean> | null | undefined
}

const isClientNonEmpty = (client: Client): boolean => {
  return !!(client['email'] && !!client['first_name'] && !!client['last_name'])
}

type Props = {
  location: Location
}

const LibrariesLibraryClientsInvite = (props: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { libraryId } = useParams<{ libraryId: string }>()
  const libraryIdNum: number = parseInt(libraryId!, 10)
  const formState: LibraryClientsInviteState = useSelector(
    (state: State) => state.forms.libraryClientsInviteForm,
  )
  const [clients, setClients] = useState([EMPTY_CLIENT])
  const [message, setMessage] = useState('')
  useEffect(() => {
    setClients(
      clients.map((client, index) => ({
        ...client,
        errors: formState.errors['clients_list']
          ? formState.errors['clients_list'][index]
          : {},
      })),
    )
  }, [formState])
  useEffect(() => {
    return () => {
      dispatch(clientLibraryClientsResetErrors(libraryIdNum))
    }
  }, [])

  const handleCancel = () => {
    navigate(
      props.location.state?.returnTo ?? `/catalogs/${libraryIdNum}/clients`,
    )
  }

  const handleSubmit = (event: React.SyntheticEvent<any>) => {
    event.preventDefault()
    const validatedClients = clients.map((client, index) => ({
      ...client,
      errors: {
        ...client.errors,
        ...Object.keys(client).reduce((errors, key) => {
          if (!client[key as keyof Client] && key !== 'errors') {
            return {
              ...errors,
              [key]: 'This field may not be blank.',
            }
          }

          return errors
        }, {} as Record<string, string>),
      } as Record<string, boolean | string>,
    }))
    setClients(validatedClients)

    if (validatedClients.every(client => isEmpty(client.errors))) {
      dispatch(
        inviteClientLibraryClients(libraryIdNum, {
          content_message: message.trim() || undefined,
          clients_list: clients.map(client => ({
            email: client.email,
            // TODO: Make first_name and last_name optional
            first_name: client.first_name || ' ',
            last_name: client.last_name || ' ',
          })),
        }),
      )
      mixpanel.sendPureEvent('Invites sent')
    }
  }

  const handleInputChange = (
    event: React.SyntheticEvent<HTMLInputElement>,
    index: number,
  ) => {
    const { name, value } = event.target as HTMLInputElement
    const update = {
      ...clients[index],
      [name]: value,
      errors: clients[index].errors ? omit(clients[index].errors, [name]) : {},
    }
    setClients(updateItem(clients, index, update))
  }

  const addClient = (event: React.SyntheticEvent<any>) => {
    event.preventDefault()
    setClients(clients.concat(EMPTY_CLIENT))
  }

  const deleteClient = (event: React.SyntheticEvent<any>, index: number) => {
    event.preventDefault()
    setClients(deleteItem(clients, index))
  }

  const getClientsList = () => clients.filter(isClientNonEmpty)

  const renderRow = (client: Client, index: number) => (
    <Row key={index} columnsSize={[5, 3, 3, 1]}>
      <div>
        <TextField
          required
          name="email"
          label={index === 0 && 'Email Address'}
          value={client['email']}
          invalid={client.errors?.email}
          error={client.errors?.email ? 'Email is required' : undefined}
          onChange={event => handleInputChange(event, index)}
        />
      </div>
      <div>
        <TextField
          required
          name="first_name"
          label={index === 0 && 'First name'}
          value={client['first_name']}
          invalid={client.errors?.first_name}
          error={
            client.errors?.first_name ? 'First name is required' : undefined
          }
          onChange={event => handleInputChange(event, index)}
        />
      </div>
      <div>
        <TextField
          required
          name="last_name"
          label={index === 0 && 'Last name'}
          value={client['last_name']}
          invalid={client.errors?.last_name}
          error={client.errors?.last_name ? 'Last name is required' : undefined}
          onChange={event => handleInputChange(event, index)}
        />
      </div>
      <div>
        {index === 0 && <Label caption="Action" hidden />}
        <Button
          appearance="icon"
          disabled={clients.length === 1}
          icon="cancel"
          onClick={event => deleteClient(event, index)}
        />
      </div>
    </Row>
  )

  return (
    <ModalWindow
      header={<Heading size={700}>Invite Clients to Catalog</Heading>}
      footer={
        formState.errors['non_field_errors'] && (
          <ErrorText>{formState.errors['non_field_errors']}</ErrorText>
        )
      }
      actions={
        <ButtonContainer>
          <Button appearance="link" caption="Cancel" onClick={handleCancel} />
          <Button
            appearance="primary"
            caption="Invite"
            disabled={getClientsList().length === 0}
            progressCaption="Inviting"
            progressIndicator={formState.inProgress}
            onClick={handleSubmit}
          />
        </ButtonContainer>
      }
      isOpen={true}
      onClose={handleCancel}
    >
      <DocumentHeadTags title="Invite Clients to Catalog | DISCO" />
      {clients.map(renderRow)}
      <Row columnsSize={[12]}>
        <div>
          <Button caption="Add another client" onClick={addClient} />
        </div>
      </Row>
      <TextField
        label="Custom Message"
        multiline={5}
        value={message}
        invalid={formState.errors['content_message']}
        error={formState.errors['content_message']}
        onChange={event => setMessage(event.target.value)}
      />
    </ModalWindow>
  )
}

export default LibrariesLibraryClientsInvite
